import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Image from 'components/Common/Image/Image'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import PaletteSheet from 'components/Luxkit/PaletteSheet'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { isString } from 'lib/string/stringUtils'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import useHoverImageSwap from 'hooks/useHoverImageSwap'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import clsx from 'clsx'

export const CAROUSEL_CARD_WIDTH = 320

const CarouselCardContainer = styled(ClickableLink)`
  display: grid;
  grid-template-rows: ${rem(200)} 1fr;
  gap: ${rem(16)};
  height: 100%;
  position: relative;
  min-height: ${rem(520)};

  ${mediaQueryUp.desktop} {
    grid-template-rows: ${rem(280)} 1fr;
  }
`

const ImageContainer = styled(PaletteSheet)`
  position: relative;
`

const BaseImage = styled(Image)`
  transition: opacity 0.5s;
  opacity: 1;
  
  &.hidden {
    opacity: 0;
  }
`

const HoverImage = styled(Image)`
  position: absolute;
  top: 0;
  left: 0;
  transition: opacity 0.5s;
  opacity: 0;
  
  &.visible {
    opacity: 1;
  }
`

const ImageHoverWrapper = styled.div`
  /* Make it behave exactly like the content inside */
  display: contents;
`

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: grid;
  grid-template-columns: auto auto;
  grid-template-areas: "left right";
  grid-template-rows: 1fr;
  padding: ${rem(8)} ${rem(12)};
  pointer-events: none;

  & > .left {
    justify-self: flex-start;
    align-self: flex-start;
    grid-area: left;
  }

  & > .right {
    justify-self: flex-end;
    align-self: flex-start;
    grid-area: right;
  }

  a, button {
    pointer-events: auto;
  }
`

interface Props {
  image: App.Image;
  hoverImage?: App.Image;
  location: string | React.ReactElement;
  title: string;
  providerName?: string;
  to: string;
  className?: string;
  priceDetails?: React.ReactElement;
  bookmarkButton?: React.ReactElement;
  urgencyLabels?: React.ReactElement;
  imageCornerLabel?: React.ReactElement;
  description?: string | React.ReactElement;
  rating?: App.OfferRating
  'data-testid'?: string;
  onClick?: () => void;
  timeRemainingLabel?: App.OfferUrgencyLabel;
  partnershipLabel?: React.ReactElement;
  loyaltyPointsElement?: React.ReactElement;
  /**
   * Being used by cro_inclusion_chips_on_homepage_hero_carousels AB test (CRO-5502)
   */
  taglineInclusions?: React.ReactElement;
}

function CarouselCardMedium(props: Props) {
  const {
    bookmarkButton,
    image,
    hoverImage,
    location,
    providerName,
    title: heading,
    priceDetails: offerPriceDetails,
    to: offerUrl,
    onClick,
    rating,
    urgencyLabels,
    className,
    description,
    'data-testid': dataTestId,
    timeRemainingLabel,
    partnershipLabel,
    imageCornerLabel,
    loyaltyPointsElement,
    taglineInclusions,
  } = props

  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  const handleImageLoaded = useCallback(() => {
    dispatchOfferListEvent({ type: OfferListEvents.offerReady })
  }, [dispatchOfferListEvent])

  const isDesktop = useScreenSizeUp('tablet')
  const isHoverEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.croChangeImageOnHover, isDesktop && !!hoverImage)
  const { hovered, onMouseEnter, onMouseLeave } = useHoverImageSwap(isHoverEnabled && !!hoverImage)

  return (
    <CarouselCardContainer
      data-testid={dataTestId}
      onClick={onClick}
      target={config.OPEN_NEW_TAB_OFFER_CLICK ? '_blank' : undefined}
      to={offerUrl}
      className={className}
    >
      <ImageHoverWrapper
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >

        <ImageContainer paletteType="default">
          <BaseImage
            className={clsx({ hidden: hovered && hoverImage })}
            aspectRatio="3:2"
            image={image}
            fit="center"
            dpr={2}
            height="280"
            width="512"
            onLoad={handleImageLoaded}
          />
          {hoverImage && (
            <HoverImage
              className={clsx({ visible: hovered })}
              aspectRatio="3:2"
              image={hoverImage}
              fit="center"
              dpr={2}
              height="280"
              width="512"
              onLoad={handleImageLoaded}
            />
          )}
          <ImageOverlay>
            <LabelGroup className="left">
              {timeRemainingLabel?.end && <OfferUrgencyCountdownLabel
                endDate={timeRemainingLabel.end}
                tagPlace="carousel-card-medium"
              />}
              {!timeRemainingLabel && imageCornerLabel}
              {partnershipLabel}
            </LabelGroup>
            <TripGuard>
              <div className="right">
                {bookmarkButton}
              </div>
            </TripGuard>
          </ImageOverlay>
        </ImageContainer>
      </ImageHoverWrapper>
      <Group direction="vertical" verticalAlign="space-between" gap={12}>
        <VerticalSpacer gap={8}>
          <Group direction="horizontal" gap={8} horizontalAlign="space-between">
            <Group direction="vertical">
              {isString(location) ? <BodyText colour="neutral-one" variant="medium">{location}</BodyText> : location}
              {providerName && <BodyText colour="neutral-one" variant="medium" weight="bold">{providerName}</BodyText>}
            </Group>
            {rating && <OfferRating
              variant="xs"
              rating={rating}
              onlyNumber="auto"
              inlineLabel
              hideLink
            />}
          </Group>
          <Group direction="vertical" gap={4}>
            <BodyText
              colour="neutral-one"
              variant="large"
              weight="bold"
              lineClamp={3}
              desktopLineClamp={2}
            >
              {heading}
            </BodyText>
            {taglineInclusions}
          </Group>
          {description && isString(description) && <BodyText colour="neutral-two" variant="medium">{description}</BodyText>}
          {description && !isString(description) && description}
          {loyaltyPointsElement}
          {urgencyLabels}
        </VerticalSpacer>
        <div>
          {offerPriceDetails}
        </div>
      </Group>
    </CarouselCardContainer>
  )
}

export default CarouselCardMedium
